import { useId, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { InfoTip } from "./InfoTip";

export interface SettingsListProps {
  /** Optional heading above the list, for a tab holding more than one list. */
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * One framed list of settings, one per row. The standard layout for settings
 * windows and panels: each row names the setting on the left and holds its
 * control on the right, so a column of settings scans like a table.
 *
 * The frame is a hairline only, without a fill: the list takes the surface it
 * sits on (a white panel, the page background of a settings form), so it never
 * reads as a separate card. Its title uses the same scale as a field label.
 */
export function SettingsList({ title, children, className }: SettingsListProps) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cn("space-y-1.5", className)}
    >
      {title ? (
        <h3 id={headingId} className="text-xs font-medium text-secondary">
          {title}
        </h3>
      ) : null}
      <div className="divide-y divide-border overflow-hidden rounded-panel border border-border">
        {children}
      </div>
    </section>
  );
}

export interface SettingsRowProps {
  label: ReactNode;
  /** One short line: what the setting changes. */
  description?: ReactNode;
  /** Links the label to a native form control inside the row. */
  htmlFor?: string;
  /**
   * `"inline"` (default) puts the control on the right. `"stacked"` puts it under
   * the label, full width — for text fields and lists that need the room.
   */
  layout?: "inline" | "stacked";
  /**
   * `"tooltip"` moves the description behind a small info icon next to the
   * label and tightens the row — a plain key / value line for settings whose
   * explanation is worth having but not worth the space.
   */
  descriptionAs?: "text" | "tooltip";
  /** Names the setting for the info tip when `label` is not plain text. */
  name?: string;
  children: ReactNode;
  className?: string;
}

export function SettingsRow({
  label,
  description,
  htmlFor,
  layout = "inline",
  descriptionAs = "text",
  name,
  children,
  className,
}: SettingsRowProps) {
  const LabelTag = htmlFor ? "label" : "p";
  const compact = descriptionAs === "tooltip";
  const labelText = (
    <LabelTag {...(htmlFor ? { htmlFor } : {})} className="block text-sm font-medium text-primary">
      {label}
    </LabelTag>
  );
  const text =
    compact && description ? (
      <div className="flex min-w-0 items-center gap-1">
        {labelText}
        <InfoTip label={name ?? (typeof label === "string" ? label : "Details")}>{description}</InfoTip>
      </div>
    ) : (
      <div className="min-w-0">
        {labelText}
        {description ? <p className="mt-0.5 text-xs leading-5 text-muted">{description}</p> : null}
      </div>
    );
  return layout === "stacked" ? (
    <div className={cn(compact ? "space-y-2 px-4 py-2.5" : "space-y-2.5 px-4 py-3", className)}>
      {text}
      {children}
    </div>
  ) : (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4",
        compact ? "min-h-11 py-2" : "min-h-14 py-2.5",
        className,
      )}
    >
      {text}
      <div className="ml-auto flex shrink-0 items-center">{children}</div>
    </div>
  );
}
